<script setup lang="ts">
// The propose → preview → confirm handshake in one dialog.
//
// This is the feature's REUSABLE seam (exported from `index.ts`): it takes a workspace id and an
// optional prefill, so the day a campaign list lands in v4, a row's "change budget" / "pause"
// action mounts this same component with `initial` set instead of growing a second flow.
import { computed, watch } from 'vue'

import { useProposeAction } from '../composables/useProposeAction'
import type { ProposeActionInput } from '../models/platformAction.types'

import ActionPreviewDiff from './ActionPreviewDiff.vue'
import ProposeActionForm from './ProposeActionForm.vue'
import ProposeActionResult from './ProposeActionResult.vue'

import { useI18n } from '@/shared/i18n'
import { Alert, Button, Modal } from '@/shared/ui'

const open = defineModel<boolean>({ default: false })

const props = defineProps<{
  workspaceId: number | null
  /** Prefill for a caller that already knows the campaign (a future row action). */
  initial?: Partial<ProposeActionInput>
}>()

const emit = defineEmits<{
  /** A request was confirmed — the host reloads its list. */
  confirmed: []
  /**
   * This proposal collided with one the workspace already has. The handoff's rule is "resume that
   * one, don't re-propose", so the host opens it rather than this modal growing a second detail view.
   */
  resume: [requestId: string]
}>()

const { t } = useI18n()
const {
  input,
  step,
  fieldErrors,
  error,
  errorText,
  violations,
  isStale,
  duplicate,
  candidates,
  chooseAdAccount,
  proposal,
  result,
  isBusy,
  isPolling,
  submit: runPropose,
  confirm: runConfirm,
  back,
  reset,
} = useProposeAction()

const canSubmit = computed(() => props.workspaceId !== null && !isBusy.value)

// Each opening is a fresh handshake: a token from a previous session is spent or stale, and a
// leftover preview would invite approving something the user did not just review.
watch(open, (isOpen) => {
  reset()
  if (isOpen && props.initial) input.value = { ...input.value, ...props.initial }
})

async function submit(): Promise<void> {
  if (props.workspaceId === null) return
  await runPropose(props.workspaceId)
}

async function confirm(): Promise<void> {
  if (props.workspaceId === null) return
  if (await runConfirm(props.workspaceId)) emit('confirmed')
}

/** The user names the account the backend refused to guess, and the proposal is retried with it. */
async function pickAccount(adAccountId: string): Promise<void> {
  if (props.workspaceId === null) return
  await chooseAdAccount(props.workspaceId, adAccountId)
}

/** Hand the collided request to the host and get out of the way — never re-propose it here. */
function resume(): void {
  const target = duplicate.value
  if (!target) return
  open.value = false
  emit('resume', target.requestId)
}
</script>

<template>
  <Modal v-model="open" size="lg" :title="t('platformActions.propose.title')">
    <div class="propose-modal flex flex-col gap-md font-sans">
      <!-- A stale token is not a failure, it is a refresh prompt (acceptance criterion 2). -->
      <Alert v-if="isStale" tone="warning" :title="t('platformActions.propose.staleTitle')">
        {{ t('platformActions.propose.staleBody') }}
      </Alert>
      <Alert v-else-if="error" tone="danger" :title="errorText">
        <ul v-if="violations.length > 0" class="propose-modal__violations m-0 ps-md">
          <li v-for="violation in violations" :key="violation">{{ violation }}</li>
        </ul>
        <Button v-if="duplicate" size="sm" variant="outline" @click="resume">
          {{ t('platformActions.propose.resume') }}
        </Button>

        <!-- The backend refused to guess between several ad accounts; naming one answers it. -->
        <div
          v-if="candidates.length > 0"
          class="propose-modal__candidates flex flex-wrap items-center gap-sm"
        >
          <p class="propose-modal__hint m-0 text-muted text-[0.875rem]">
            {{ t('platformActions.propose.pickAccount') }}
          </p>
          <Button
            v-for="candidate in candidates"
            :key="candidate.adAccountId"
            size="sm"
            variant="outline"
            :disabled="isBusy"
            @click="pickAccount(candidate.adAccountId)"
          >
            {{ candidate.name }}
          </Button>
        </div>
      </Alert>

      <ProposeActionForm
        v-if="step === 'form'"
        v-model="input"
        :errors="fieldErrors"
        :disabled="isBusy"
      />

      <template v-else-if="step === 'preview'">
        <ActionPreviewDiff :preview="proposal?.request.preview ?? null" />
        <p class="propose-modal__hint m-0 text-muted text-[0.875rem]">
          {{ t('platformActions.preview.expiresHint') }}
        </p>
      </template>

      <ProposeActionResult v-else-if="result" :request="result" :is-polling="isPolling" />
    </div>

    <template #footer>
      <div class="propose-modal__actions flex justify-end gap-sm">
        <template v-if="step === 'form'">
          <Button variant="ghost" @click="open = false">
            {{ t('platformActions.propose.cancel') }}
          </Button>
          <Button :loading="isBusy" :disabled="!canSubmit" @click="submit">
            {{
              isStale
                ? t('platformActions.propose.refreshPreview')
                : t('platformActions.propose.submit')
            }}
          </Button>
        </template>

        <template v-else-if="step === 'preview'">
          <Button variant="ghost" :disabled="isBusy" @click="back">
            {{ t('platformActions.propose.back') }}
          </Button>
          <Button :loading="isBusy" :disabled="!canSubmit" @click="confirm">
            {{ t('platformActions.propose.confirm') }}
          </Button>
        </template>

        <Button v-else @click="open = false">{{ t('platformActions.propose.close') }}</Button>
      </div>
    </template>
  </Modal>
</template>
